import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FileText, Plus, Trash2 } from "lucide-react";
import axios from "axios";
import favicon from "../assets/favicon_new.png";

// Prevent re-fetching on remounts within the same session and cache data
let __templatesFetchedOnce = false;
let __templatesCache = null;

const TemplateSection = ({ 
  isDarkMode, 
  themeClasses, 
  setShowCreateTemplate, 
  setShowEditTemplate, 
  setSelectedTemplate 
}) => {
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);

  // On mount: hydrate from cache if available; otherwise fetch once
  useEffect(() => {
    if (__templatesFetchedOnce && Array.isArray(__templatesCache)) {
      setTemplates(__templatesCache);
      setTemplatesLoading(false);
      setTemplatesError(null);
    } else {
      fetchTemplates();
    }
  }, []);

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const token = localStorage.getItem("authToken");

      // Check if token exists
      if (!token) {
        return;
      }

      const response = await axios.get("/api/templates", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setTemplates(response.data);
      __templatesCache = response.data;
      setTemplatesError(null);
      __templatesFetchedOnce = true;
    } catch (error) {
      console.error("Error fetching templates:", error);
      setTemplatesError(error.message);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    console.log("Selected template for editing:", template);
    setSelectedTemplate(template);
    setShowEditTemplate(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(`/api/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Refresh templates list
      const updated = templates.filter((t) => t.id !== templateId);
      setTemplates(updated);
      __templatesCache = updated;
    } catch (err) {
      console.error("Error deleting template:", err);
      alert("Failed to delete template");
    }
  };

  if (templatesLoading) {
    return (
      <div className="space-y-8 animate-in fade-in duration-500">
        <div className="flex items-center justify-center p-12">
          <div className="relative">
            <div className="relative w-7 h-7">
              <motion.div
                className="w-full h-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 2,
                  ease: "linear",
                  repeat: Infinity,
                }}
              >
                <img
                  src={favicon}
                  alt="Loading"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    console.log("Image failed to load:", e.target.src);
                    e.target.style.display = "none";
                  }}
                />
              </motion.div>
            </div>
          </div>
          <span className="ml-4 text-slate-600 font-medium">
            Loading templates...
          </span>
        </div>
      </div>
    );
  }

  if (templatesError) {
    return (
      <div className="space-y-8 animate-in fade-in duration-500">
        <div className="max-w-md mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">
              Error Loading Templates
            </h3>
            <p className="text-red-600 text-sm">{templatesError}</p>
            <button
              onClick={fetchTemplates}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Enhanced Header Section */}
      <div className="text-center space-y-4 max-w-3xl mx-auto">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-500 shadow-lg mb-4">
          <FileText className="w-8 h-8 text-white" />
        </div>
        <h2
          className={`text-3xl font-bold ${
            isDarkMode ? "text-white" : "text-slate-800"
          } bg-clip-text `}
        >
          Document Templates
        </h2>
        
        
        {/* Refresh Button */}
        <button
          onClick={fetchTemplates}
          disabled={templatesLoading}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            isDarkMode
              ? "bg-gray-700 hover:bg-gray-600 text-white"
              : "bg-gray-100 hover:bg-gray-200 text-gray-700"
          } ${templatesLoading ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <svg
            className={`w-4 h-4 ${templatesLoading ? "animate-spin" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {templatesLoading ? "Refreshing..." : "Refresh Templates"}
        </button>
      </div>

      {/* Enhanced Templates Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {/* Enhanced Add New Template Card */}
        <div
          className={`group cursor-pointer rounded-2xl border-2 border-dashed  hover:border-blue-400 ${
            isDarkMode
              ? "bg-gray-800/95 text-white border-white/20"
              : "bg-white/95 text-slate-600 border-slate-280"
          } hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg`}
          onClick={() => setShowCreateTemplate(true)}
        >
          <div className="flex flex-col items-center justify-center p-8 min-h-[240px] space-y-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 group-hover:from-blue-100 group-hover:to-indigo-100 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
              <Plus className="w-8 h-8 text-slate-500 group-hover:text-blue-600 group-hover:rotate-90 transition-all duration-300" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-lg font-bold  group-hover:text-blue-600 transition-colors duration-300">
                Create Template
              </h3>
              <p className="text-sm  group-hover:text-blue-500 transition-colors duration-300 max-w-xs">
                Build a custom template for your document type
              </p>
            </div>
          </div>
        </div>

        {/* Enhanced Template Cards */}
        {templates?.map((template, index) => (
          <div
            key={template.id}
            className={`group cursor-pointer relative ${
              isDarkMode
                ? "bg-gray-800/95 border-slate-950"
                : "bg-white/95 border-slate-280"
            } rounded-2xl border hover:border-slate-280 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 overflow-hidden animate-in slide-in-from-bottom-4`}
            style={{ animationDelay: `${index * 100}ms` }}
            onClick={(e) => {
              e.stopPropagation();
              handleEditTemplate(template);
            }}
          >
            {/* Card Header */}
            <div className="p-8 pb-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-1 min-w-0">
                  <h3
                    className={`text-xl truncate leading-tight line-clamp-2 font-bold ${
                      isDarkMode ? "text-white" : "text-slate-800"
                    } group-hover:text-blue-600 transition-colors duration-300 mb-3 `}
                  >
                    {template.name}
                  </h3>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-slate-600"
                    } leading-relaxed line-clamp-1`}
                  >
                    {template.description || "No description provided"}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-8 pb-10"></div>

            {/* Card Footer */}
            <div
              className={`px-8 py-5 ${
                isDarkMode
                  ? "bg-gray-800/95  border-slate-950"
                  : "bg-white/95 border-slate-280"
              } border-t backdrop-blur-sm`}
            >
              <div className="flex items-center justify-between">
                <div
                  className={`inline-flex items-center gap-2 px-4 py-2 ${
                    isDarkMode ? "bg-gray-700/50" : "bg-slate-100"
                  } rounded-full`}
                >
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span
                    className={`text-xs font-semibold ${
                      isDarkMode ? "text-gray-300" : "text-slate-700"
                    }`}
                  >
                    {template.fields?.length || 0}{" "}
                    {template.fields?.length === 1 ? "field" : "fields"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTemplate(template.id);
                    }}
                    className="group/btn inline-flex items-center justify-center w-9 h-9 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 hover:scale-105"
                  >
                    <Trash2 className="w-4 h-4 group-hover/btn:scale-110 transition-transform duration-200" />
                  </button>
                </div>
              </div>
            </div>

            {/* Hover Overlay Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TemplateSection;
